import * as React from "react";
import { useHistory, Redirect } from "react-router-dom";

import { api } from "../api";
import { User } from "../models/users";
import { TestIds } from "../testIds";
import { setUser, useUser } from "../state";
import { RLink } from "../DesignSystem/RLink";

export interface RegisterData {
  username: string;
  password: string;
  email: string;
  invited_by: string;
  send_email: boolean;
}

export function RegisterPage(): JSX.Element {
  const user = useUser();
  const history = useHistory();
  const [form, setForm] = React.useState<RegisterData>({
    username: "",
    password: "",
    email: "",
    invited_by: "",
    send_email: true,
  });
  const [submitting, setSubmitting] = React.useState(false);

  const update = (key: keyof RegisterData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const register = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    const result = await api.post("/api/register", form);
    setSubmitting(false);

    if (!api.isErrorToast(result)) {
      const newUser = await api.get("/api/user");
      if (!newUser) return;

      setUser({ type: "logged-in", user: new User(newUser) });
      history.push("/feed");
    }
  };

  // Redirect to feed if user is logged in
  if (user.type === "logged-in") {
    return <Redirect to="/feed" />;
  }

  return (
    <div className="px-10 pt-10">
      <div className="mx-auto max-w-[400px]">
        <h2 className="mb-4 text-2xl font-semibold text-gray-800 dark:text-gray-100">
          Sign up
        </h2>
        <div className="rounded-lg border-b-[3px] border-[#e0dcdc] bg-white p-6 shadow-sm dark:border dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Username
            </label>
            <div className="relative">
              <i
                className="fa fa-user absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              ></i>
              <input
                onChange={(e) => update("username", e.currentTarget.value)}
                type="text"
                name="username"
                data-testid={TestIds.RegisterUsernameInput}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Enter your username"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <div className="relative">
              <i
                className="fa fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              ></i>
              <input
                onChange={(e) => update("password", e.currentTarget.value)}
                type="password"
                name="password"
                data-testid={TestIds.RegisterPasswordInput}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            <div className="relative">
              <i
                className="fa fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              ></i>
              <input
                onChange={(e) => update("email", e.currentTarget.value)}
                type="email"
                name="email"
                data-testid={TestIds.RegisterEmailInput}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Invited by{" "}
              <span className="text-gray-400 dark:text-gray-500">
                (optional)
              </span>
            </label>
            <div className="relative">
              <i
                className="fa fa-user-plus absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              ></i>
              <input
                onChange={(e) => update("invited_by", e.currentTarget.value)}
                type="text"
                name="invited_by"
                data-testid={TestIds.RegisterInvitedByInput}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Who invited you?"
              />
            </div>
          </div>

          <div className="mb-6 flex items-center gap-3">
            <input
              onChange={(e) => update("send_email", e.target.checked)}
              checked={form.send_email}
              type="checkbox"
              name="send_email"
              id="send_email"
              data-testid={TestIds.ForgotPasswordSubmit}
              className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
            />
            <label
              htmlFor="send_email"
              className="text-sm text-gray-700 dark:text-gray-300"
            >
              Email me notifications of activity on my entries
            </label>
          </div>

          <button
            data-testid={TestIds.RegisterSubmit}
            type="submit"
            disabled={submitting}
            onClick={() => register()}
            className="w-full rounded-md bg-white px-4 py-2 text-lg font-medium text-slate-900 shadow transition hover:bg-slate-100 disabled:opacity-50"
          >
            {submitting ? "Creating account..." : "Create Account"}
          </button>

          <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{" "}
            <RLink
              className="text-blue-600 hover:underline dark:text-blue-400"
              to="/login"
            >
              Log in
            </RLink>
          </div>
        </div>
      </div>
    </div>
  );
}
